import { countries } from '@/app/account/update-my-details/client/countries'
import { getTopics } from '@/app/account/update-my-details/client/topics'
import { AccountFormState } from '../shared/account-form-state'

export type AccountField = {
  label?: string
  type?: 'text' | 'email' | 'select' | 'checkbox' | 'grouped_checkbox' | 'radio' | 'textarea'
  required?: boolean
  choices?: { label: string; value: string }[] | string[]
  charLimit?: number
  placeholder?: string
  classNames?: {
    field?: string
    input?: string
  }
}

export interface AccountFields {
  first_name?: AccountField
  last_name?: AccountField
  job_title?: AccountField
  phone?: AccountField
  city?: AccountField
  country?: AccountField
  custom_feed_topics?: AccountField
}

/**
 * --------------------------------------------------------------------------
 * Example usage including conditional logic:
 *
 * const formDataObject = Object.fromEntries(formData) as Record<string, string>
 *
 * const accountFields = await getAccountFields(null).then((fields) =>
 *   applyConditionalLogic(fields, formDataObject),
 * );
 * --------------------------------------------------------------------------
 **/
export const getAccountFields = async (displayFields = {} as AccountFields) => {
  const allFields = {
    first_name: {
      label: 'First Name',
      type: 'text',
      placeholder: 'Enter your first name',
      required: true,
    },
    last_name: {
      label: 'Last Name',
      type: 'text',
      required: true,
    },
    job_title: {
      label: 'Job Title',
      type: 'text',
    },
    phone: {
      label: 'Phone',
      type: 'text',
    },
    city: {
      label: 'City',
      type: 'text',
      charLimit: 40,
    },
    country: {
      label: 'Country',
      type: 'select',
      choices: countries,
      required: true,
    },
    custom_feed_topics: {
      label: 'Topics',
      type: 'grouped_checkbox',
      choices: await getTopics(),
      classNames: {
        field: 'col-span-2',
        input: 'grid grid-cols-2 gap-x-s gap-y-xxs s:grid-cols-1 [&_label]:font-normal',
      },
    },
  } as AccountFields

  // Consolidate fields based on displayFields
  const outputFields = consolidateFields(allFields, displayFields)

  return outputFields
}

/**
 * --------------------------------------------------------------------------
 * consolidateFields
 *
 * This function takes the complete set of account fields and the display fields
 * specified by the user. It returns a new object that contains only the fields
 * that are specified in the display fields, while also merging any additional
 * properties from the display fields into the original fields.
 * --------------------------------------------------------------------------
 **/
function consolidateFields(allFields: AccountFields, displayFields: AccountFields): AccountFields {
  // If no custom display fields are provided, return the default fields
  if (!displayFields || Object.keys(displayFields).length === 0) {
    return allFields
  }

  // Use the displayFields to reduce, override or extend the original fields
  return Object.keys(displayFields).length
    ? Object.keys(displayFields).reduce((acc, key) => {
        acc[key] = {
          ...allFields[key as string],
          ...displayFields[key as string],
        }
        return acc
      }, {} as AccountFields)
    : allFields
}

/**
 * --------------------------------------------------------------------------
 * applyConditionalLogic
 *
 * This function applies conditional logic to the account fields based on the
 * current form data. For example, if the country is Jersey, it makes the city
 * field optional.
 *
 * This function is run on mount and the form change event to dynamically
 * adjust the fields based on user input.
 * --------------------------------------------------------------------------
 **/
export const applyConditionalLogic = (
  accountFields: AccountFields,
  formData: AccountFormState | Record<string, string>,
) => {
  // If the country is Jersey, make city field optional
  const isJersey = formData?.country?.toLowerCase() === 'jersey'

  if (accountFields?.city) {
    accountFields.city.required = isJersey ? false : true
  }

  // Return as a new object to ensure changes are deteced in React state
  return { ...accountFields }
}
